'use client';

import { useTranslation } from '@/contexts/dictionaryContext';
import useFilterContext from '@/hooks/useFilterContext';
import { useLocale } from '@/hooks/useLocale';
import { VillageProductReview } from '@/services/types';
import { useEffect, useState } from 'react';
import ReviewsSectionItem from './Item';
import ReviewsSectionPagination from './Pagination';

type Props = {
  className?: string;
  productReviews: VillageProductReview[];
  isExperience?: boolean;
};

export default function ReviewsSectionList({
  className,
  productReviews,
  isExperience = false,
}: Props) {
  const { village: dict } = useTranslation();
  const lang = useLocale();

  const { reviewsLanguageFilter } = useFilterContext();
  const [filteredReviews, setFilteredReviews] = useState(productReviews);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginatedReviews, setPaginatedReviews] = useState(
    filteredReviews.slice(0, 10),
  );

  const pagesTotal = Math.ceil(filteredReviews.length / 10);

  function handleNextPage() {
    const startIndex = currentPage * 10;
    const endIndex = startIndex + 10;

    const nextPageContent = filteredReviews.slice(startIndex, endIndex);
    setPaginatedReviews(nextPageContent);
    setCurrentPage((prev) => prev + 1);
  }

  function handlePreviousPage() {
    const startIndex = (currentPage - 2) * 10;
    const endIndex = startIndex + 10;

    const previousPageContent = filteredReviews.slice(startIndex, endIndex);
    setPaginatedReviews(previousPageContent);
    setCurrentPage((prev) => prev - 1);
  }

  function handleGoToPage(page: number) {
    const startIndex = (page - 1) * 10;
    const endIndex = startIndex + 10;

    const selectedPageContent = filteredReviews.slice(startIndex, endIndex);
    setPaginatedReviews(selectedPageContent);
    setCurrentPage(page);
  }

  useEffect(() => {
    const reviews = isExperience
      ? productReviews
      : productReviews.filter((review) => review.productApproval === true);

    const filteredReviews = reviews
      .filter((review) => {
        if (!reviewsLanguageFilter) {
          return review;
        }

        if (reviewsLanguageFilter !== 'all' && reviewsLanguageFilter) {
          return review?.authorLanguage?.code === reviewsLanguageFilter;
        }

        return review;
      })
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );

    setFilteredReviews(filteredReviews);
    setPaginatedReviews(filteredReviews.slice(0, 10));
    //eslint-disable-next-line
  }, [reviewsLanguageFilter]);

  return (
    <>
      <div className={className}>
        {paginatedReviews.map((review, index) => (
          <ReviewsSectionItem
            dict={dict}
            showResponse={review.adminContentResponse}
            lang={lang}
            className="py-[40px] px-[10px] border-t border-gray-300 first:border-t-0 desktop:px-[110px]"
            review={review}
            key={index}
          />
        ))}
      </div>
      <ReviewsSectionPagination
        handlePageClick={handleGoToPage}
        handleGoToNextPage={handleNextPage}
        handleGoToPreviousPage={handlePreviousPage}
        currentPage={currentPage}
        pagesTotal={pagesTotal}
        className="mt-0 flex flex-wrap justify-center desktop:mt-[60px]"
      />
    </>
  );
}
